import debounce from 'lodash-es/debounce';
import type { PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigation, useSearchParams } from 'react-router';

import { defaultStoreSwitcherContext } from '@jane/bloom/hooks/storeSwitcher/defaultContext';
import { useGooglePlaces } from '@jane/bloom/hooks/useGooglePlaces';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { registerSuperPropertiesStore } from '@jane/bloom/services/tracking/mixpanel';
import type { FulfillmentMode } from '@jane/bloom/types/schemas/bloom/fulfillmentMode';
import type {
  AutocompleteAddress,
  StoreSwitcher,
} from '@jane/bloom/types/schemas/bloom/storeSwitcher';
import type { PremiumReservationMode } from '@jane/bloom/types/schemas/marketplace/reservationMode';
import {
  RESERVATION_MODE_QUERY_KEY,
  SELECT_STORE_QUERY_KEY,
} from '@jane/bloom/utils/queryParams/queryParams';

import { filteredAndSortedStores } from './filteredAndSortedStores';
import { useDeliveryValidation } from './useDeliveryValidation';
import { usePinStore } from './usePinStore';
import { useStoreSwitcherSearchState } from './useStoreSearchState';

const StoreSwitcherContext = createContext<StoreSwitcher>(
  defaultStoreSwitcherContext
);

const retrieveReservationModeParam = (
  reservationModeParam: string | null
): PremiumReservationMode | undefined => {
  if (!reservationModeParam) {
    return undefined;
  }
  const lowerCaseParam = reservationModeParam.toLowerCase();
  if (lowerCaseParam === 'delivery' || lowerCaseParam === 'pickup') {
    return lowerCaseParam;
  }
  return undefined;
};

export function StoreSwitcherProvider({ children }: PropsWithChildren) {
  const { getAddressDetails, getGoogleAddresses, validateLocation } =
    useGooglePlaces();
  const {
    pinnedStore,
    shoppingPreferences: savedShoppingPreferences,
    stores,
  } = useRootLoaderData();
  const [isVisible, setIsVisible] = useState(false);
  const { pinning: manuallyPinning, pinStore } = usePinStore();
  const {
    canDeliver,
    deliverableStoreIds,
    hasValidated,
    validateDelivery,
    validationState,
  } = useDeliveryValidation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [shouldPinStore, setShouldPinStore] = useState(true);
  const reservationModeParam = retrieveReservationModeParam(
    searchParams.get(RESERVATION_MODE_QUERY_KEY)
  );
  const selectStoreParam = searchParams.get(SELECT_STORE_QUERY_KEY);
  const isSelectStoreTrue = selectStoreParam === 'true';

  const [busy, setBusy] = useState(false);
  const [reservationModeWarningShown, setReservationModeWarningShown] =
    useState(false);
  const [isUndeliverableAddress, setIsUndeliverableAddress] = useState(false);
  const [inDeliveryRange, setInDeliveryRange] = useState(canDeliver);
  const [locationButtonDisabled, setLocationButtonDisabled] = useState(false);
  const [fulfillmentMode, setFulfillmentMode] =
    useState<FulfillmentMode>('both');

  const { state: navigationState } = useNavigation();
  const isNavigating = navigationState === 'loading';
  const isLoadingRef = useRef(false);

  const {
    shoppingPreferences: searchShoppingPreferences,
    updateShoppingPreferences,
  } = useStoreSwitcherSearchState({
    initialShoppingPreferences: savedShoppingPreferences,
  });
  const {
    latitude: searchLatitude,
    longitude: searchLongitude,
    reservationMode: searchReservationMode,
    userEnteredAddress: searchAddress,
  } = searchShoppingPreferences;

  const {
    availableStores,
    currentStore,
    deliveryStores,
    medStores,
    pickupStores,
    recStores,
  } = filteredAndSortedStores(stores, {
    currentPinnedStore: pinnedStore,
    deliverableStoreIds,
    fulfillmentMode,
    reservationMode: searchReservationMode,
    searchAddressCoords: {
      lat: searchLatitude ?? savedShoppingPreferences?.latitude ?? undefined,
      long: searchLongitude ?? savedShoppingPreferences?.longitude ?? undefined,
    },
  });

  const setPinnedStore = (
    storeId: number,
    reservationMode?: PremiumReservationMode
  ) => {
    const newPinnedStore = availableStores.find((s) => s.id === storeId);
    const isDeliverySupported = newPinnedStore && newPinnedStore?.delivery;
    const newReservationMode = reservationMode ?? searchReservationMode;

    if (newReservationMode === 'delivery' && !isDeliverySupported) {
      setReservationModeWarningShown(true);
    } else {
      setBusy(true);
      pinStore({
        ...searchShoppingPreferences,
        lat:
          searchShoppingPreferences.latitude ??
          savedShoppingPreferences?.latitude,
        long:
          searchShoppingPreferences.longitude ??
          savedShoppingPreferences?.longitude,
        reservationMode: newReservationMode,
        storeId,
      });
      registerSuperPropertiesStore(newPinnedStore);
      setBusy(false);
    }
  };

  const [addressSuggestions, setAddressSuggestions] = useState<
    AutocompleteAddress[]
  >([]);

  const validateAddress = async (
    address: string,
    newReservationMode?: PremiumReservationMode
  ) => {
    const currentReservationMode = newReservationMode ?? searchReservationMode;

    if (address === '') {
      return setIsUndeliverableAddress(true);
    }

    try {
      const response = await validateLocation(address, currentReservationMode);
      if (response.error) {
        return setIsUndeliverableAddress(true);
      } else {
        setIsUndeliverableAddress(false);
        if (currentReservationMode === 'delivery') {
          validateDelivery({
            address,
          });
        }
      }
    } catch {
      setIsUndeliverableAddress(true);
    }
  };

  const handleSearchReservationModeChanged = (
    newReservationMode: PremiumReservationMode
  ) => {
    if (newReservationMode === 'pickup') {
      setIsUndeliverableAddress(false);
      setInDeliveryRange(true);
    } else {
      validateAddress(searchAddress, newReservationMode);
    }
    updateShoppingPreferences({ reservationMode: newReservationMode });
  };

  const handleSearchAddressChanged = debounce(async (value: string) => {
    let currentSuggestions = [];

    updateShoppingPreferences({ userEnteredAddress: value });
    setInDeliveryRange(true);

    if (value === '') {
      setAddressSuggestions([]);
      if (searchReservationMode === 'delivery') {
        setIsUndeliverableAddress(true);
      } else {
        setIsUndeliverableAddress(false);
      }

      const {
        city,
        coordinates,
        countryCode,
        state,
        street,
        street2,
        zipcode,
      } = await getAddressDetails(value);
      updateShoppingPreferences({
        city,
        countryCode,
        latitude: coordinates.lat,
        longitude: coordinates.long,
        state,
        streetAddress: street,
        streetAddress2: street2,
        zipcode: zipcode ?? '',
      });
    } else {
      const addresses = await getGoogleAddresses(value);
      setAddressSuggestions(addresses);
      currentSuggestions = addresses;
    }

    if (
      currentSuggestions.length === 0 &&
      searchReservationMode === 'delivery'
    ) {
      validateAddress(value);
    }
  }, 500);

  const toggleVisibility = useCallback(() => {
    setIsVisible(!isVisible);

    // Remove select-store query parameter when closing the store switcher
    if (isVisible && isSelectStoreTrue) {
      setSearchParams(
        (prev) => {
          prev.delete(SELECT_STORE_QUERY_KEY);
          return prev;
        },
        { preventScrollReset: true }
      );
    }
  }, [isVisible, isSelectStoreTrue, setSearchParams]);

  const displayAddressSuggestions =
    addressSuggestions.length > 0 && searchAddress.length > 0;

  useEffect(() => {
    if (validationState === 'idle' && searchReservationMode === 'delivery') {
      if (canDeliver || canDeliver === undefined || displayAddressSuggestions) {
        setInDeliveryRange(true);
      } else {
        setInDeliveryRange(false);
      }
    }
  }, [
    canDeliver,
    validationState,
    searchReservationMode,
    displayAddressSuggestions,
  ]);

  const selectSuggestedAddress = async (address: string) => {
    setAddressSuggestions([]);
    validateAddress(address);

    const { city, coordinates, countryCode, state, street, street2, zipcode } =
      await getAddressDetails(address);
    updateShoppingPreferences({
      city,
      countryCode,
      latitude: coordinates.lat,
      longitude: coordinates.long,
      state,
      streetAddress: street,
      streetAddress2: street2,
      userEnteredAddress: address,
      zipcode: zipcode ?? '',
    });
  };

  useEffect(() => {
    if (isSelectStoreTrue) {
      setIsVisible(true);
    }
  }, [isSelectStoreTrue]);

  useEffect(() => {
    if (!reservationModeParam) {
      return;
    }
    const openStoreSwitcher = () => {
      setShouldPinStore(false);
      setIsVisible(true);
      handleSearchReservationModeChanged(reservationModeParam);
    };

    if (
      !savedShoppingPreferences?.hasPinnedStoreId ||
      !currentStore ||
      isSelectStoreTrue
    ) {
      openStoreSwitcher();
      return;
    }

    const currentStoreSupportsDesiredMode =
      (currentStore.delivery && reservationModeParam === 'delivery') ||
      (currentStore.pickup && reservationModeParam === 'pickup');

    if (
      currentStoreSupportsDesiredMode &&
      shouldPinStore &&
      (reservationModeParam === 'pickup' ||
        deliverableStoreIds?.includes(String(currentStore.id)))
    ) {
      setPinnedStore(currentStore.id, reservationModeParam);
      handleSearchReservationModeChanged(reservationModeParam);
    } else if (
      reservationModeParam === 'delivery' &&
      !hasValidated &&
      !!searchAddress
    ) {
      validateDelivery({
        address: searchAddress,
      });
    } else {
      openStoreSwitcher();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    reservationModeParam,
    deliverableStoreIds,
    hasValidated,
    isSelectStoreTrue,
  ]);

  useEffect(() => {
    // Handle closing the store switcher when page is finished navigating away
    if (isNavigating && isVisible && !isLoadingRef.current) {
      isLoadingRef.current = true;
    }

    if (!isNavigating && isVisible && isLoadingRef.current) {
      isLoadingRef.current = false;

      if (!isSelectStoreTrue && !reservationModeParam) {
        toggleVisibility();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNavigating, isVisible, toggleVisibility]);

  return (
    <StoreSwitcherContext.Provider
      value={{
        addressSuggestions,
        availableStores,
        busy,
        deliveryStores,
        displayAddressSuggestions,
        fulfillmentMode,
        handleSearchAddressChanged,
        handleSearchReservationModeChanged,
        hasPinnedStoreId: !!savedShoppingPreferences?.hasPinnedStoreId,
        inDeliveryRange,
        isUndeliverableAddress,
        isVisible,
        locationButtonDisabled,
        medStores,
        pickupStores,
        pinnedStore: currentStore ?? pinnedStore,
        pinning: manuallyPinning,
        recStores,
        reservationModeWarningShown,
        searchAddress,
        searchReservationMode,
        selectSuggestedAddress,
        setAddressSuggestions,
        setBusy,
        setFulfillmentMode,
        setLocationButtonDisabled,
        setPinnedStore,
        setReservationModeWarningShown,
        toggleVisibility,
        updateShoppingPreferences,
        validateAddress,
        validationState,
      }}
    >
      {children}
    </StoreSwitcherContext.Provider>
  );
}

export const useStoreSwitcher = () => useContext(StoreSwitcherContext);
